import { useEffect, useRef, useState } from 'react';
import { GOJUON_CATEGORIES } from '../../utils/gojuon';

type SortOrder = 'created_at' | 'title' | 'rating';

interface AuthorFilterProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  selectedRating: number | null;
  onRatingChange: (rating: number | null) => void;
  sortOrder?: SortOrder;
  onSortCycle?: () => void;
}

// ソートアイコン
function SortIcon({ sortOrder }: { sortOrder: SortOrder }) {
  if (sortOrder === 'title') {
    return <span className="text-sm font-bold leading-none">T</span>;
  }
  if (sortOrder === 'rating') {
    return <span className="text-sm leading-none">★</span>;
  }
  // created_at: カレンダーアイコン
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

const RATINGS = [1, 2, 3, 4, 5];

export default function AuthorFilter({
  selectedCategory,
  onCategoryChange,
  selectedRating,
  onRatingChange,
  sortOrder = 'created_at',
  onSortCycle,
}: AuthorFilterProps) {
  const [showRating1Menu, setShowRating1Menu] = useState(false);
  const rating1Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showRating1Menu) return;
    function handleClickOutside(e: MouseEvent) {
      if (rating1Ref.current && !rating1Ref.current.contains(e.target as Node)) {
        setShowRating1Menu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRating1Menu]);

  return (
    <>
      {/* モバイル用: selectドロップダウン（md未満で表示） */}
      <div className="flex gap-2 mb-4 md:hidden">
        {/* 全クリアボタン */}
        <button
          onClick={() => {
            onCategoryChange('');
            onRatingChange(null);
          }}
          className={`px-3 py-2 rounded-lg font-medium border transition flex-shrink-0 ${
            selectedCategory === '' && selectedRating === null
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'
          }`}
        >
          *
        </button>

        {/* レーティング select */}
        <select
          value={selectedRating ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            onRatingChange(val !== '' ? Number(val) : null);
            if (val !== '') onCategoryChange('');
          }}
          className={`flex-1 rounded-lg border bg-white px-2 py-2 text-sm transition ${
            selectedRating !== null
              ? 'border-yellow-500 text-yellow-700'
              : 'border-gray-300 text-gray-700'
          }`}
        >
          <option value="">★ 評価</option>
          <option value="0">未評価</option>
          {RATINGS.map((r) => (
            <option key={r} value={r}>
              {'★'.repeat(r)} {r}
            </option>
          ))}
        </select>

        {/* 50音 select */}
        <select
          value={selectedCategory}
          onChange={(e) => {
            onCategoryChange(e.target.value);
            if (e.target.value) onRatingChange(null);
          }}
          className={`flex-1 rounded-lg border bg-white px-2 py-2 text-sm transition ${
            selectedCategory
              ? 'border-blue-600 text-blue-700'
              : 'border-gray-300 text-gray-700'
          }`}
        >
          <option value="">50音</option>
          {GOJUON_CATEGORIES.slice(1).map((category) => (
            <option key={category.value} value={category.value}>
              {category.label}
            </option>
          ))}
        </select>

        {/* ソートボタン */}
        <button
          onClick={onSortCycle}
          className="px-3 py-2 rounded-lg font-medium border transition flex-shrink-0 bg-white text-gray-700 hover:bg-gray-100 border-gray-300"
          title={sortOrder === 'created_at' ? '追加順' : sortOrder === 'title' ? 'タイトル順' : '評価順'}
        >
          <SortIcon sortOrder={sortOrder} />
        </button>
      </div>

      {/* PC用: ボタン列（md以上で表示） */}
      <div className="hidden md:flex flex-wrap gap-2 mb-6">
        {/* 最初の「*」ボタン */}
        <button
          key=""
          onClick={() => {
            onCategoryChange('');
            onRatingChange(null);
          }}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedCategory === ''
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
          }`}
        >
          *
        </button>

        {/* Rating Filter Buttons */}
        <div className="flex gap-0">
          {/* ★1: ドロップダウントリガー（未評価も選択可） */}
          <div ref={rating1Ref} className="relative">
            <button
              onClick={() => setShowRating1Menu((prev) => !prev)}
              className={`px-4 py-2 font-medium transition-colors border rounded-l-lg ${
                selectedRating === 0
                  ? 'bg-gray-400 text-white border-gray-400'
                  : selectedRating !== null && 1 <= selectedRating
                  ? 'bg-yellow-500 text-white border-yellow-500'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'
              }`}
            >
              ★
            </button>
            {showRating1Menu && (
              <div className="absolute top-full left-0 z-10 mt-1 w-20 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                <button
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                  onClick={() => { onRatingChange(selectedRating === 1 ? null : 1); setShowRating1Menu(false); }}
                >
                  ★1
                </button>
                <button
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                  onClick={() => { onRatingChange(selectedRating === 0 ? null : 0); setShowRating1Menu(false); }}
                >
                  未評価
                </button>
              </div>
            )}
          </div>

          {/* ★2〜★5 */}
          {RATINGS.slice(1).map((rating, index) => (
            <button
              key={`rating-${rating}`}
              onClick={() => onRatingChange(rating)}
              className={`px-4 py-2 font-medium transition-colors border ${
                index === RATINGS.length - 2 ? 'rounded-r-lg' : ''
              } ${
                selectedRating !== null && rating <= selectedRating
                  ? 'bg-yellow-500 text-white border-yellow-500'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'
              }`}
            >
              ★
            </button>
          ))}
        </div>

        {/* 残りの50音順ボタン（「英数」から） */}
        {GOJUON_CATEGORIES.slice(1).map((category) => (
          <button
            key={category.value}
            onClick={() => onCategoryChange(category.value)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedCategory === category.value
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            {category.label}
          </button>
        ))}

        {/* ソートボタン: 右寄せ */}
        <button
          onClick={onSortCycle}
          className="ml-auto px-4 py-2 rounded-lg font-medium border transition bg-white text-gray-700 hover:bg-gray-100 border-gray-300 flex items-center gap-1.5"
          title={sortOrder === 'created_at' ? '追加順' : sortOrder === 'title' ? 'タイトル順' : '評価順'}
        >
          <SortIcon sortOrder={sortOrder} />
        </button>
      </div>
    </>
  );
}
